import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { FaPencilAlt, FaSearch, FaTrashAlt } from 'react-icons/fa';
import FormattedDateTime from '@/shared/components/FormattedDateTime';
import DataCacheService from '@/shared/services/DataCacheService';
import RelationName from '@/shared/components/RelationName';
import AuthService, { privileges } from '@/shared/services/AuthService';
import { CanDelete, CanUpdate } from '@/shared/components/security';
import { EnvelopeIcon, ShareIcon } from '@/shared/components/Icons';
import messages from '../interactionMessages';
import InteractionIcon from '../InteractionIcon';
import Button from '@/shared/components/Button';
import ExportInteractionPdfButton from '../ExportInteractionPdfButton';
import CopyLinkToInteractionButton from '../CopyLinkToInteractionButton';
import config from '@/config';
import Checkbox from '@/shared/components/input/Checkbox';

const InteractionOverviewRow = ({
  interaction,
  intl,
  onSelect,
  onEditInteraction,
  onOpenMail,
  onSendViaEmail,
  onShareItem,
  onDeleteInteraction,
}) => {
  const {
    id,
    date,
    relationName,
    relationType,
    relationAlias,
    relationCrm,
    relationId,
    title,
    createdBy,
    interactionType,
    selected,
  } = interaction;

  const createdByUser = useMemo(() => DataCacheService.getUserName(createdBy), [createdBy]);
  const functionality =
    interaction.createdBy === AuthService.getUserId() ? 'InteractionsOwn' : 'InteractionsOthers';

  const canViewDocuments = AuthService.hasPermission({
    functionality: 'documents',
    privilege: 'read',
  });
  const isEmail = interaction.interactionType === 2;
  const url = `${config.appUrls.app}/#/interactions/${interaction.relationId}/${interaction.id}`;
  const timelineUrl = `${config.appUrls.app}/#/interactions/${interaction.relationId}/`;

  return (
    <tbody className="hover" key={id}>
      <tr>
        <td>
          <Checkbox onClick={() => onSelect(interaction)} value={selected} />
        </td>
        <td>
          <InteractionIcon type={interactionType} intl={intl} />
        </td>
        <td>
          <FormattedDateTime value={date} />
        </td>
        <td>
          {relationId ? (
            <RelationName
              relation={{
                name: relationName,
                type: relationType,
                alias: relationAlias,
                crm: relationCrm,
                id: relationId,
              }}
            />
          ) : null}
        </td>
        <td>
          {isEmail ? (
            <a onClick={onOpenMail} className="bold" title={intl.formatMessage(messages.OpenEmail)}>
              {title}
            </a>
          ) : (
            <a
              onClick={onEditInteraction}
              className="bold"
              title={intl.formatMessage(messages.OpenInteraction)}
            >
              {title}
            </a>
          )}
        </td>
        <td>{createdByUser}</td>
        <td className="actions-column hidden-xs col-fit">
          <div className="btn-group btn-group-sm">
            {isEmail ? (
              <Button onClick={onOpenMail} title={intl.formatMessage(messages.OpenEmail)}>
                <FaSearch className="react-icon" />
              </Button>
            ) : (
              <Button onClick={onEditInteraction}>
                <CanUpdate on={functionality} fallback={<FaSearch className="react-icon" />}>
                  <FaPencilAlt className="react-icon" />
                </CanUpdate>
              </Button>
            )}

            <a
              href={timelineUrl}
              className="btn btn-default"
              title={intl.formatMessage(messages.OpenTimeline)}
            >
              <i className="fa fa-comments-o" />
            </a>
            {!isEmail || canViewDocuments ? (
              <>
                <ExportInteractionPdfButton
                  interaction={interaction}
                  intl={intl}
                  btnClassName="hidden-sm hidden-xs hidden-md"
                />
                <Button
                  title={intl.formatMessage(messages.SendInPdfViaEmail)}
                  btnClassName="hidden-sm hidden-xs hidden-md"
                  onClick={onSendViaEmail}
                >
                  <EnvelopeIcon />
                </Button>
                <CopyLinkToInteractionButton
                  interaction={interaction}
                  intl={intl}
                  btnClassName="hidden-sm hidden-xs hidden-md"
                />
              </>
            ) : null}
            <a
              href={url}
              className="btn btn-default hidden-sm hidden-xs hidden-md"
              title={intl.formatMessage(messages.GotoInteraction)}
            >
              <i className="fa fa-external-link" />
            </a>
            <Button title={intl.formatMessage(messages.Share)} onClick={onShareItem}>
              <ShareIcon />
            </Button>
            <CanDelete on={functionality}>
              <Button title={intl.formatMessage(messages.Delete)} onClick={onDeleteInteraction}>
                <FaTrashAlt />
              </Button>
            </CanDelete>
          </div>
        </td>
      </tr>
    </tbody>
  );
};
export default InteractionOverviewRow;
